// "Make it yours" (LOOK_CONTRACT.md): the business's own tweaks on top of a
// theme, stored as web_config.look. Pure functions, so tests run them under
// plain node. Legacy themes (Flat, Glass, Night Watch, classic-*) are never
// touched: every function here is a no-op for them.
import {FONT_PAIRINGS, LOOK_HERO_LAYOUT, LOOK_OPTIONS} from "./catalogueData.js";
import {isHex} from "./colour.js";
import {paletteColours} from "./palette.js";
import {themeRecord} from "./tokens.js";

const SANS = "ui-sans-serif, system-ui, sans-serif";
const SERIF = "ui-serif, Georgia, serif";
const SERIF_FAMILIES = ["Lora", "Libre Bodoni", "Zilla Slab"];

// Known keys with known values only; anything else is ignored (bristol refuses
// it on save). Missing backgrounds means tinted.
export function cleanLook(look) {
    const out = {backgrounds: "tinted"};
    if (!look || typeof look !== "object" || Array.isArray(look)) return out;
    for (const [key, allowed] of Object.entries(LOOK_OPTIONS)) {
        if (allowed.includes(look[key])) out[key] = look[key];
    }
    return out;
}

// The hero layouts a theme offers, in the look's own names (what the editor
// shows). Mirrored to tokyo through themeCatalogue.json (defaults.lookHeroes).
export function lookHeroAllowed(themeName) {
    const allowed = themeRecord(themeName).hero.allowed;
    return LOOK_OPTIONS.hero.filter((k) => allowed.includes(LOOK_HERO_LAYOUT[k]));
}

// The renderer's hero layout for the chosen look, or "" (theme default) when
// there's no choice, the theme is legacy, or it doesn't allow that layout.
export function lookHeroLayout(themeName, look) {
    const t = themeRecord(themeName);
    if (t.legacy) return "";
    const chosen = cleanLook(look).hero;
    return chosen && lookHeroAllowed(themeName).includes(chosen) ? LOOK_HERO_LAYOUT[chosen] : "";
}

export function pairingFor(look) {
    const key = cleanLook(look).fonts;
    return FONT_PAIRINGS.find((p) => p.key === key) || null;
}

// Families to load for the chosen pairing (null: use the theme's own).
export function lookFontFamilies(themeName, look) {
    if (themeRecord(themeName).legacy) return null;
    return pairingFor(look)?.families || null;
}

// ---- Your colours become the theme (palette.js) ----
// "theme" keeps the theme's own colours; anything else builds the chosen
// palette (light unless the look says rich or dark) from the owner's two colours.
// Null for legacy themes, "theme", or when the owner hasn't chosen both colours.
export function effectivePalette(t, look, brand = {}) {
    if (t.legacy) return null;
    const l = cleanLook(look);
    if (l.backgrounds === "theme") return null;
    if (!isHex(brand?.primary) || !isHex(brand?.accent)) return null;
    return l.palette || "light";
}

// ---- The override layer ----
const CORNERS = {
    sharp: {r: "2px", sm: "2px", lg: "4px", btn: "2px", badge: "2px"},
    soft: {r: "10px", sm: "6px", lg: "18px", btn: "6px", badge: "4px"},
    round: {r: "22px", sm: "14px", lg: "34px", btn: "999px", badge: "999px"},
};
const SPACING = {
    compact: {pad: "clamp(2rem, 4.5vw, 3.75rem)", gap: "1rem"},
    standard: {pad: "clamp(3rem, 7vw, 6rem)", gap: "1.5rem"},
    airy: {pad: "clamp(4.25rem, 10vw, 8.5rem)", gap: "2.25rem"},
};
const PHOTOS = {
    natural: {radius: "0px", filter: "none", border: "0", shadow: "none"},
    rounded: {radius: "var(--t-radius-lg)", filter: "none", border: "0", shadow: "none"},
    framed: {radius: "var(--t-radius-sm)", filter: "none", border: "8px solid var(--t-surface)", shadow: "var(--t-shadow)"},
    // Greyscale here; the primary colour is laid over it in theme.css
    // (html[data-photos="duotone"]).
    duotone: {radius: "var(--t-radius)", filter: "grayscale(1) contrast(1.05)", border: "0", shadow: "none"},
};

// Adds the look's CSS variables to `vars` (the theme's tokens), in place.
export function applyLook(t, vars, look, brand = {}) {
    if (t.legacy) return vars;
    const l = cleanLook(look);
    const palette = effectivePalette(t, l, brand);
    if (palette) Object.assign(vars, paletteColours(t, brand, palette)?.vars);
    const pair = pairingFor(l);
    if (pair) {
        const serif = SERIF_FAMILIES.includes(pair.display) ? SERIF : SANS;
        const display = `'${pair.display}', ${serif}`;
        const body = `'${pair.body}', ${SANS}`;
        Object.assign(vars, {
            "--t-font-display": display, "--t-font-body": body, "--t-font-accent": display,
            "--t-display-weight": String(pair.displayWeight), "--t-display-tracking": pair.displayTracking,
            "--t-display-case": pair.displayCase,
            "--theme-font-family": body, "--theme-font-family-display": display,
        });
    }
    const c = CORNERS[l.corners];
    if (c) {
        Object.assign(vars, {
            "--t-radius": c.r, "--t-radius-sm": c.sm, "--t-radius-lg": c.lg,
            "--t-card-radius": c.r, "--t-btn-radius": c.btn, "--t-badge-radius": c.badge,
        });
    }
    const s = SPACING[l.spacing];
    if (s) Object.assign(vars, {"--t-section-pad": s.pad, "--t-gap": s.gap});
    const ph = PHOTOS[l.photos];
    if (ph) {
        Object.assign(vars, {
            "--t-photo-radius": ph.radius, "--t-photo-filter": ph.filter, "--t-photo-border": ph.border,
            "--t-photo-shadow": ph.shadow, "--t-photo-rotate": "0deg",
        });
    }
    return vars;
}

// The "we nudged a colour" notes for the Look panel (empty when none, or when
// the palette isn't in use). Sent to tokyo by the preview bridge.
export function paletteNotes(themeName, brand, look) {
    const t = themeRecord(themeName);
    const palette = effectivePalette(t, look, brand);
    return palette ? paletteColours(t, brand, palette)?.notes || [] : [];
}

// Attributes the look adds to the page root (none for legacy themes).
export function lookAttributes(themeName, look, brand = {}) {
    const t = themeRecord(themeName);
    if (t.legacy) return {};
    const l = cleanLook(look);
    const out = {};
    const palette = effectivePalette(t, l, brand);
    if (palette) out["data-palette"] = palette;
    if (l.motion) out["data-motion"] = l.motion === "off" ? "none" : l.motion;
    if (l.photos) out["data-photos"] = l.photos;
    if (lookHeroLayout(themeName, l)) out["data-hero"] = l.hero;
    return out;
}
